/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import {
  Box,
  Container,
  Flex,
  Grid,
  IntrinsicImage,
  Text,
  Link,
  TishmanThemeName,
  SxStyleProp,
  AnchorSection,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import React, { memo, useCallback, useMemo } from 'react';
import { IGatsbyImageData } from 'gatsby-plugin-image';

import { useLocalizedLink } from '../utils/use-localized-link';

/* eslint-disable react/prop-types */
interface DescriptionChildren {
  text: string;
}

export interface Description {
  children: DescriptionChildren[];
}

export interface Url {
  url: string;
  caption: string;
}

export interface TicketOfferingTicket {
  title: string;
  url: Url;
  image: {
    alt: string;
    asset: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
  description: Description[];
  slugId: string;
  order: number;
}

export interface TicketOfferingProps {
  includedTicketsArray: TicketOfferingTicket[];
  title?: string;
  sx?: SxStyleProp;
  id: string;
  theme?: TishmanThemeName;
  description?: string;
  link?: string;
}

interface TicketCardProps {
  offering: TicketOfferingTicket;
  i: number;
}

export function TicketOfferings({
  includedTicketsArray,
  title,
  sx,
  id,
  description,
  link,
  theme = 'Rock Center Cream',
}: TicketOfferingProps): React.ReactNode {
  const getLocalizedLink = useLocalizedLink();

  const getGridTemplateColumns = useCallback((length: number) => {
    if (length % 3 === 0) return ['1fr', '1fr 1fr', '1fr 1fr 1fr'];
    if (length % 2 === 0) return ['1fr', '1fr 1fr'];
    return ['1fr'];
  }, []);

  const isSpecialCase = useMemo(() => {
    return (
      includedTicketsArray &&
      (includedTicketsArray.length === 5 || includedTicketsArray.length === 7)
    );
  }, [includedTicketsArray]);

  if (includedTicketsArray.length === 0) {
    return null;
  }

  const TicketCard: React.FC<TicketCardProps> = memo(({ offering, i }) => (
    <Box
      bg="white"
      key={i}
      sx={{
        willChange: 'transform',
        transform: 'translateZ(0)',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box>
          {offering.image?.asset?.gatsbyImageData && (
            <img
              alt={offering.image.alt}
              src={
                offering.image?.asset?.gatsbyImageData?.images?.fallback?.src
              }
              style={{
                width: '100%',
                height: 'auto',
                aspectRatio: '316/212',
              }}
            />
          )}
        </Box>
        <Box sx={{ flex: '1 1 auto', pt: 4, px: 3 }}>
          <H
            sx={{
              variant: 'styles.h2',
              fontFamily: 'headingSecondary',
              fontSize: 5,
              fontWeight: 'heading',
              color: 'black',
            }}
          >
            {offering.title}
          </H>
          <Box sx={{ pb: 3, pt: 4 }}>
            <Text sx={{ fontSize: 3, color: 'black', overflow: 'auto' }}>
              {offering.description[0].children.map((child, index) => (
                <React.Fragment key={index}>{child.text}</React.Fragment>
              ))}
            </Text>
          </Box>
        </Box>
        <Box sx={{ mt: 'auto', pb: [0, 4], px: [0, 3] }}>
          <Link
            sx={{
              display: 'block',
              textAlign: 'center',
              width: '100%',
              bg: 'black',
              color: 'white',
            }}
            to={getLocalizedLink(offering.url.url)}
            variant="button"
          >
            Buy Tickets
          </Link>
        </Box>
      </Flex>
    </Box>
  ));

  TicketCard.displayName = 'TicketCard';

  const MemoizedSection = memo(AnchorSection);

  return (
    <MemoizedSection id={id} theme={theme}>
      <Container paddingY={5}>
        {/* HEADER SECTION: TITLE, DESCRIPTION, AND NAV LINKS */}
        <Flex
          sx={{
            flexDirection: ['column', 'row'],
            justifyContent: 'space-between',
            mb: [2, null, 4],
            pt: [0, 5],
          }}
        >
          {title && (
            <H
              sx={{
                variant: 'styles.h1',
                fontFamily: 'headingSecondary',
              }}
            >
              {title}
            </H>
          )}
          {description && (
            <Box
              sx={{
                fontSize: [2, 3],
                letterSpacing: [2, 1],
                opacity: 0.8,
                display: ['none', null, 'block'],
                ...sx,
              }}
            >
              {description}
            </Box>
          )}
          <Flex sx={{ flexDirection: ['column', 'row'] }}>
            {link?.includes('tor=true') && (
              <Link
                sx={{ maxHeight: 45, maxWidth: 165, mt: [3, 0] }}
                to={
                  '/attractions/top-of-the-rock-observation-deck/plan-your-visit/?tab=compare-tickets'
                }
                variant="underline"
              >
                Compare Tickets
              </Link>
            )}
            {link && (
              <Link
                sx={{
                  maxHeight: 45,
                  maxWidth: [100, 'none'],
                  ml: [0, 5],
                  mt: [3, 0],
                }}
                to={link ?? '/buy-tickets'}
                variant="underline"
              >
                See All
                <Icons.Arrow
                  sx={{ display: ['inline-block', 'none'], ml: 2 }}
                />
              </Link>
            )}
          </Flex>
        </Flex>

        {/* TICKET OFFERINGS GRID */}
        <Grid
          gap={3}
          px={[0, 4]}
          py={4}
          sx={{
            gridTemplateColumns: getGridTemplateColumns(
              includedTicketsArray?.length,
            ),
          }}
        >
          {includedTicketsArray.map(
            (offering: TicketOfferingTicket, i: number) => {
              if (
                isSpecialCase &&
                includedTicketsArray.length === 7 &&
                i === 6
              ) {
                return (
                  <Box
                    backgroundColor="white"
                    color="black"
                    key={i}
                    sx={{
                      gridColumn: ['1 / span 1', '1 / span 2', '1 / span 3'],
                      display: 'flex',
                      flexDirection: ['column', null, 'row'],
                    }}
                  >
                    <Box sx={{ minWidth: ['320px', '360px'], height: '100%' }}>
                      <IntrinsicImage
                        alt={offering.image.alt}
                        image={offering.image.asset.gatsbyImageData}
                        ratio={[238 / 160, 316 / 212]}
                      />
                    </Box>
                    <Box
                      padding={4}
                      paddingBottom={[4, 0]}
                      sx={{ minWidth: '200px' }}
                    >
                      <H
                        sx={{
                          variant: 'styles.h2',
                          fontFamily: 'headingSecondary',
                          fontSize: 4,
                          fontWeight: 'heading',
                          color: 'black',
                        }}
                      >
                        {offering.title}
                      </H>
                    </Box>
                    <Box paddingX={[4, 4, 0]} paddingY={[0, 4]}>
                      <Box
                        paddingBottom={[0, 4, 0]}
                        paddingRight={4}
                        sx={{ color: 'black', flex: 1 }}
                      >
                        {offering.description[0].children[0].text}
                      </Box>
                      <Box paddingBottom={4}>
                        <Link
                          sx={{
                            'display': 'block',
                            'maxWidth': '115px',
                            'pt': 3,
                            'textAlign': 'left',
                            'color': 'black',
                            '::after': {
                              backgroundColor: 'black',
                              right: '10px',
                            },
                          }}
                          to={getLocalizedLink(offering.url.url)}
                          variant="underline"
                        >
                          Learn More
                        </Link>
                      </Box>
                    </Box>
                  </Box>
                );
              }
              return <TicketCard i={i} key={i} offering={offering} />;
            },
          )}
        </Grid>
      </Container>
    </MemoizedSection>
  );
}
